"use client";

import { Heart, Search } from "lucide-react";
import dynamic from "next/dynamic";
import { useRef, useState } from "react";
import { useFavorites } from "@/hooks/useFavorites";

const QuickViewModal = dynamic(
  () => import("./QuickViewModal").then((module) => module.QuickViewModal),
  { ssr: false },
);

interface ProductCardActionsProps {
  productId: number;
  productSlug: string;
  productName: string;
  /**
   * Quantas etiquetas há no canto superior DIREITO da imagem (desconto,
   * "Em falta"): o coração desce para logo abaixo delas. 0 = no topo.
   */
  topRightBadges?: number;
}

export function ProductCardActions({
  productId,
  productSlug,
  productName,
  topRightBadges = 0,
}: ProductCardActionsProps) {
  const quickViewTriggerRef = useRef<HTMLButtonElement>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(productId);
  const favoriteLabel = favorited
    ? "Remover dos favoritos"
    : "Adicionar aos favoritos";

  return (
    <>
      <div className={`absolute right-2 ${topRightBadges >= 2 ? "top-[4.25rem]" : topRightBadges === 1 ? "top-10" : "top-2"} z-20 flex flex-col items-end gap-2 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100`}>
        <div className="group/action relative">
          <button
            type="button"
            onClick={() => void toggleFavorite(productId)}
            aria-label={`${favoriteLabel}: ${productName}`}
            aria-pressed={favorited}
            title={favoriteLabel}
            className="flex h-[35px] w-[35px] items-center justify-center rounded-xl bg-white/60 text-primary shadow-sm transition-colors hover:bg-white hover:text-secondary active:bg-white active:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:h-[37px] md:w-[37px]"
          >
            <Heart
              className={`h-4 w-4 transition-transform duration-200 md:h-[17px] md:w-[17px] ${
                favorited ? "scale-110 fill-secondary text-secondary" : ""
              }`}
              aria-hidden="true"
            />
          </button>
          <span
            role="tooltip"
            className="pointer-events-none absolute right-full top-1/2 mr-2 hidden -translate-y-1/2 whitespace-nowrap rounded-xl bg-slate-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity md:block md:group-hover/action:opacity-100 md:group-focus-within/action:opacity-100"
          >
            {favoriteLabel}
          </span>
        </div>

        <div className="group/action relative hidden md:block">
          <button
            ref={quickViewTriggerRef}
            type="button"
            onClick={() => setIsQuickViewOpen(true)}
            aria-label={`Visualização rápida: ${productName}`}
            aria-haspopup="dialog"
            aria-expanded={isQuickViewOpen}
            title="Visualização rápida"
            className="flex h-[37px] w-[37px] items-center justify-center rounded-xl bg-white/60 text-primary shadow-sm transition-colors hover:bg-white hover:text-secondary active:bg-white active:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Search className="h-[17px] w-[17px]" aria-hidden="true" />
          </button>
          <span
            role="tooltip"
            className="pointer-events-none absolute right-full top-1/2 mr-2 hidden -translate-y-1/2 whitespace-nowrap rounded-xl bg-slate-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity md:block md:group-hover/action:opacity-100 md:group-focus-within/action:opacity-100"
          >
            Visualização rápida
          </span>
        </div>
      </div>

      {isQuickViewOpen ? (
        <QuickViewModal
          productSlug={productSlug}
          onClose={() => setIsQuickViewOpen(false)}
          returnFocusRef={quickViewTriggerRef}
        />
      ) : null}
    </>
  );
}
